import { LegalShell } from "@/components/legal/LegalShell";

const LAST_UPDATED = "October 12, 2026";

const SECTIONS = [
  {
    id: "the-tone",
    title: "The tone we want",
    body: (
      <>
        <p>
          Last Bookmark is a small, deliberate community of authors and beta readers. We're
          here to help each other tell better stories — that means treating people the way we'd
          want to be treated: warmly, honestly, and with respect.
        </p>
        <p>
          These Guidelines are a promise you make to the rest of the community when you use
          Last Bookmark. Please read them.
        </p>
      </>
    ),
  },
  {
    id: "respect",
    title: "Be respectful to every user",
    body: (
      <>
        <ul className="list-disc pl-6 space-y-1.5">
          <li>
            No harassment, targeted insults, threats, doxxing, stalking, or discrimination on
            the basis of race, ethnicity, national origin, religion, gender identity, sexual
            orientation, disability, age, or any other protected characteristic.
          </li>
          <li>Feedback should be honest but kind — critique the work, not the person.</li>
          <li>Assume good faith. Ask before you accuse. Escalate to us if you need help.</li>
        </ul>
      </>
    ),
  },
  {
    id: "confidentiality",
    title: "Confidentiality of manuscripts and shared content",
    body: (
      <>
        <p>
          When another user shares a manuscript, sample chapter, outline, feedback document, or
          any other creative work with you on Last Bookmark, it is <strong>confidential</strong>.
          You may read it, take notes on it for the purpose of giving feedback, and reference
          it in your review — nothing more.
        </p>
        <p>Specifically, you agree that you will not:</p>
        <ul className="list-disc pl-6 space-y-1.5">
          <li>Copy, print, screenshot, or otherwise reproduce another user's manuscript except as strictly needed for your beta-read notes.</li>
          <li>Share, forward, or upload another user's manuscript to any other person, platform, or service.</li>
          <li>Publish, quote, or otherwise disclose the plot, characters, twists, or other private content of another user's work without their explicit written permission.</li>
          <li>Use another user's manuscript, feedback, or private content to train a model, feed a scraper, or generate derivative content of any kind.</li>
        </ul>
        <p>
          Authors keep <strong>full ownership</strong> of their work. Beta readers keep full
          ownership of the feedback they write. Neither party's contribution can be used
          without the creator's permission. Respect for authors' intellectual property is a
          non-negotiable expectation of every user.
        </p>
      </>
    ),
  },
  {
    id: "beta-readers-no-ai",
    title: "For beta readers: personal reading, no AI",
    body: (
      <>
        <p>
          Authors come to Last Bookmark for genuine human feedback. Every beta reader agrees to
          this clause as part of the reader confidentiality agreement (version 2.0) before
          opening a manuscript:
        </p>
        <blockquote className="border-l-4 pl-4 italic space-y-3">
          <p>I agree to personally read the manuscript and provide feedback based on my own reading and judgment.</p>
          <p>
            I will not use AI to generate beta reading feedback, reports, or reviews. I will not
            upload, paste, or otherwise share the author&rsquo;s manuscript, excerpts, or
            confidential project materials with AI tools or services.
          </p>
          <p>Violations may result in suspension or permanent removal from Last Bookmark.</p>
        </blockquote>
        <p>
          This applies on every project, alongside the confidentiality rules above. Suspected
          violations can be reported like any other breach and are handled under
          {" "}<a href="#enforcement" className="link-underline">Enforcement</a>.
        </p>
      </>
    ),
  },
  {
    id: "authors-before-sharing",
    title: "For authors: before you share your manuscript",
    body: (
      <>
        <p>
          Last Bookmark requires readers to respect manuscript confidentiality and prohibits
          AI-generated beta reading feedback and sharing manuscript content with AI tools or
          services. However, we cannot monitor every reader&rsquo;s activity outside the platform
          or guarantee that a reader will comply with these rules.
        </p>
        <p>
          Before sharing your work, use Coffee Chats and messaging to discuss the reader&rsquo;s
          experience, feedback process, and how they will handle your manuscript. Share only when
          you feel comfortable proceeding.
        </p>
        <p>
          If you suspect misuse, contact{" "}
          <a href="mailto:hello@lastbookmark.com" className="link-underline">hello@lastbookmark.com</a>.
          We take reports seriously, review the available information, and may suspend or remove
          users found to have violated our rules.
        </p>
      </>
    ),
  },
  {
    id: "honesty",
    title: "Be honest about who you are and what you do",
    body: (
      <>
        <ul className="list-disc pl-6 space-y-1.5">
          <li>Represent yourself truthfully — real name or pen name is fine; fake identities used to deceive others are not.</li>
          <li>Describe your experience and specialisations honestly on your profile and in Certification applications.</li>
          <li>Deliver the work you agreed to deliver. If you can't, communicate early.</li>
          <li>Don't create fake reviews, fake applications, or fake activity on the platform.</li>
        </ul>
      </>
    ),
  },
  {
    id: "no-scams",
    title: "No scams, fraud, or off-platform pressure",
    body: (
      <>
        <ul className="list-disc pl-6 space-y-1.5">
          <li>Don't solicit or accept payment for a service you don't intend to perform.</li>
          <li>Don't pressure another user to move a payment or contract off-platform to avoid our reporting tools.</li>
          <li>Don't attempt to phish credentials, promote unrelated get-rich schemes, or run other scams targeting our community.</li>
        </ul>
        <p>
          If someone tries any of the above with you, use the{" "}
          <strong>Report user</strong> button in your conversation with them, or email us at{" "}
          <a href="mailto:hello@lastbookmark.com" className="link-underline">
            hello@lastbookmark.com
          </a>
          . Screenshots help.
        </p>
      </>
    ),
  },
  {
    id: "prohibited-content",
    title: "Prohibited content",
    body: (
      <>
        <p>
          Adult and mature fiction is welcome on Last Bookmark — that's part of who we are —
          but the following is <strong>never</strong> permitted, in manuscripts, messages,
          profiles, or anywhere else:
        </p>
        <ul className="list-disc pl-6 space-y-1.5">
          <li>Any sexual content involving minors, in any form.</li>
          <li>Content that depicts, promotes, or glorifies real-world violence against a specific person or group.</li>
          <li>Content that facilitates or encourages self-harm.</li>
          <li>Content that infringes another person's copyright, trademark, or right of publicity.</li>
          <li>Malware, phishing, or content designed to compromise other users' accounts or devices.</li>
        </ul>
      </>
    ),
  },
  {
    id: "reporting",
    title: "How to report a problem",
    body: (
      <>
        <p>
          Every user profile and every message thread has a <strong>Report user</strong>
          action. Use it whenever you see harassment, a scam attempt, an impersonation, a
          confidentiality breach, or any other violation of these Guidelines.
        </p>
        <p>You can also:</p>
        <ul className="list-disc pl-6 space-y-1.5">
          <li>Block a user to stop future messages from them (they won't be notified).</li>
          <li>Email us directly at{" "}
            <a href="mailto:hello@lastbookmark.com" className="link-underline">hello@lastbookmark.com</a>
            {" "}with details and screenshots. We treat reports seriously and confidentially.</li>
        </ul>
      </>
    ),
  },
  {
    id: "enforcement",
    title: "Enforcement",
    body: (
      <>
        <p>
          Last Bookmark reserves the right to <strong>suspend or remove accounts</strong> that
          violate these Guidelines, harass others, scam users, or create an unsafe environment.
          Depending on the severity of the violation, our response may include:
        </p>
        <ul className="list-disc pl-6 space-y-1.5">
          <li>A private warning and coaching.</li>
          <li>Content removal.</li>
          <li>Temporary suspension of specific features (posting, applying, messaging).</li>
          <li>Full account suspension or permanent removal.</li>
          <li>Referral to law enforcement in cases involving illegal content or credible threats.</li>
        </ul>
        <p>
          Certified Beta Reader status can be revoked if a certified reader violates these
          Guidelines. We aim to be fair, consistent, and — where safety allows — transparent
          about the reasons for any enforcement decision.
        </p>
      </>
    ),
  },
  {
    id: "appeals",
    title: "Appeals",
    body: (
      <>
        <p>
          If you believe we made the wrong call, email us at{" "}
          <a href="mailto:hello@lastbookmark.com" className="link-underline">
            hello@lastbookmark.com
          </a>{" "}
          within 30 days of the action. A different admin will re-review the decision.
        </p>
      </>
    ),
  },
  {
    id: "changes",
    title: "Changes to these Guidelines",
    body: (
      <>
        <p>
          These Guidelines will evolve as our community grows. Material updates will bump the
          "Last updated" date and, where appropriate, be surfaced inside the app.
        </p>
      </>
    ),
  },
];

export default function CommunityGuidelines() {
  return (
    <LegalShell
      eyebrow="Community"
      title="Community Guidelines"
      intro="How we treat each other on Last Bookmark — and the protections you can count on when you share your work here."
      lastUpdated={LAST_UPDATED}
      sections={SECTIONS}
      testid="community-guidelines"
    />
  );
}
